import {
  formatDateForDisplay,
  formatDateTimeForDisplay,
  formatTimeForDisplay,
  toIsoDateOrNow,
} from 'sharedKernel/handlers/date'
import {
  extractListRows,
  extractListTotal,
  unwrapDataPayload,
} from '../../shared/apiResponse'
import { IMeetingRoomReservationDetail } from '../../manage-meeting-room/MeetingRoomReservationsTable/types'
import {
  IMeetingRoomSolicitationDetail,
  MeetingRoomSolicitationStatus,
} from '../types'
import { ReservationStatusCode } from './reservationConstants'
import {
  ReservationMeetingRoomActionPayload,
  ReservationMeetingRoomDto,
} from './types'

const text = (value?: string | null, fallback = '-'): string => value?.trim() || fallback

const STATUS_BY_TOKEN: Record<string, MeetingRoomSolicitationStatus> = {
  [String(ReservationStatusCode.Approved)]: 'aprovada',
  '2': 'aprovada',
  aprovada: 'aprovada',
  approved: 'aprovada',
  [String(ReservationStatusCode.Rejected)]: 'reprovada',
  '3': 'reprovada',
  reprovada: 'reprovada',
  rejected: 'reprovada',
  [String(ReservationStatusCode.Cancelled)]: 'cancelada',
  '4': 'cancelada',
  cancelada: 'cancelada',
  cancelled: 'cancelada',
  canceled: 'cancelada',
}

const mapStatus = (status: unknown): MeetingRoomSolicitationStatus =>
  STATUS_BY_TOKEN[String(status ?? '').toLowerCase()] ?? 'pendente'

const formatAudienceLabel = (dto: ReservationMeetingRoomDto): string => {
  if (dto.otherAudience?.trim()) {
    return dto.otherAudience.trim()
  }

  const audience = dto.targetAudience ?? []
  if (audience.length > 0) {
    return audience.map(item => String(item)).join(', ')
  }

  return '-'
}

export const mapReservationMeetingRoomDtoToDetail = (
  item: unknown
): IMeetingRoomSolicitationDetail | null => {
  const dto = unwrapDataPayload<ReservationMeetingRoomDto>(item)
  if (dto == null) return null

  const id = dto.id != null ? String(dto.id) : ''
  if (!id) return null

  const startTime = formatTimeForDisplay(dto.startTime)
  const endTime = formatTimeForDisplay(dto.endTime)
  const reservationDate = formatDateForDisplay(dto.reservationDate)
  const endDate = formatDateForDisplay(dto.endDate) || reservationDate

  return {
    id,
    meetingRoomId: dto.meetingRoomId != null ? String(dto.meetingRoomId) : '',
    requesterName: text(dto.companyName?.trim() || dto.name),
    space: text(dto.meetingRoomName),
    reservationDate,
    timeRange: startTime && endTime ? `${startTime} - ${endTime}` : '-',
    requesterType: 'externo',
    requestDate: formatDateTimeForDisplay(dto.createdAt ?? dto.dataEnvio),
    status: mapStatus(dto.status),
    requesterFullName: text(dto.name),
    requesterWhatsapp: text(dto.phone?.trim() || dto.phoneNumber),
    requesterEmail: text(dto.email),
    requesterInstitutionType: dto.institutionType?.trim() || formatAudienceLabel(dto),
    requesterInstitutionName: text(dto.companyName),
    eventName: text(dto.eventName),
    startDate: reservationDate,
    startTime,
    endDate,
    endTime,
    eventDescription: text(dto.eventDescription?.trim() || dto.observation),
    eventPurpose: text(dto.purpose),
    isPublicEvent: dto.isPublic === true,
    attendeesCount: dto.numberAttendees ?? dto.numberPeople ?? 0,
    wantsDivulgation: Boolean(dto.brandUsage?.trim() || dto.isLinkedToProgram),
    eventWebsite: dto.programName?.trim() || '',
    promotionalImageName: '',
    supportDocumentName: '',
    isPaidEvent: dto.isCharging === true,
    ticketDescription: dto.chargeDescription?.trim() || '',
  }
}

export type ReservationMeetingRoomListResult = {
  rows: IMeetingRoomSolicitationDetail[]
  total: number
}

export const normalizeReservationMeetingRoomListResponse = (
  response: unknown
): IMeetingRoomSolicitationDetail[] =>
  extractListRows(response)
    .map(mapReservationMeetingRoomDtoToDetail)
    .filter((item): item is IMeetingRoomSolicitationDetail => item != null)

export const normalizeReservationMeetingRoomListResult = (
  response: unknown
): ReservationMeetingRoomListResult => {
  const rows = normalizeReservationMeetingRoomListResponse(response)
  const total = extractListTotal(response)

  return {
    rows,
    total: total > 0 ? total : rows.length,
  }
}

export const normalizeReservationMeetingRoomDetailResponse = (
  response: unknown
): IMeetingRoomSolicitationDetail | null => {
  if (response == null) return null
  return mapReservationMeetingRoomDtoToDetail(unwrapDataPayload(response) ?? response)
}

export const buildReservationMeetingRoomActionPayload = (
  solicitation: IMeetingRoomSolicitationDetail,
  options: {
    status: ReservationStatusCode
    isAdminEdit?: boolean
    meetingRoomId?: string
    roomName?: string
    startTime?: string
    endTime?: string
  }
): ReservationMeetingRoomActionPayload => ({
  id: solicitation.id,
  name: solicitation.requesterFullName,
  email: solicitation.requesterEmail,
  reservationDate: toIsoDateOrNow(solicitation.reservationDate),
  endDate: toIsoDateOrNow(solicitation.endDate || solicitation.reservationDate),
  startTime: formatTimeForDisplay(options.startTime ?? solicitation.startTime),
  endTime: formatTimeForDisplay(options.endTime ?? solicitation.endTime),
  roomName: options.roomName ?? solicitation.space,
  meetingRoomId: options.meetingRoomId ?? solicitation.meetingRoomId,
  status: options.status,
  isAdminEdit: options.isAdminEdit ?? false,
})

export const mapSolicitationToReservationDetail = (
  solicitation: IMeetingRoomSolicitationDetail
): IMeetingRoomReservationDetail => {
  const { space: _space, meetingRoomId: _meetingRoomId, ...reservation } = solicitation
  return reservation
}
